import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Projects API
export const projectsAPI = {
  getAll: () => api.get('/projects'),
  getOne: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  delete: (id) => api.delete(`/projects/${id}`),
  uploadIFC: (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${projectId}/upload-ifc`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getReport: (id) => api.get(`/projects/${id}/report`),
  downloadPdfReport: (id) => api.get(`/projects/${id}/report/pdf`, { responseType: 'blob' }),
  getBreeamAssessment: (id) => api.get(`/projects/${id}/certification/breeam`),
  getLeedAssessment: (id) => api.get(`/projects/${id}/certification/leed`),
};

// Design Options API
export const designOptionsAPI = {
  create: (projectId, data) => api.post(`/projects/${projectId}/options`, data),
  delete: (projectId, optionId) => api.delete(`/projects/${projectId}/options/${optionId}`),
  compare: (projectId, optionIds) => api.post(`/projects/${projectId}/compare`, { design_option_ids: optionIds }),
};

// Materials API
export const materialsAPI = {
  getAll: () => api.get('/materials'),
  getCategories: () => api.get('/materials/categories'),
  getOne: (id) => api.get(`/materials/${id}`),
};

// Carbon Calculation API
export const carbonAPI = {
  calculate: (materials) => api.post('/calculate', { materials }),
};

// AI Advisor API
export const advisorAPI = {
  ask: (message, projectId = null, context = null) => 
    api.post('/advisor', { message, project_id: projectId, context }),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  listUsers: () => api.get('/admin/users'),
  setUserStatus: (id, status) => api.patch(`/admin/users/${id}/status`, { status }),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  listCompanies: () => api.get('/admin/companies'),
  createCompany: (data) => api.post('/admin/companies', data),
  updateCompany: (id, data) => api.patch(`/admin/companies/${id}`, data),
  deleteCompany: (id) => api.delete(`/admin/companies/${id}`),
  getActivity: (limit = 20) => api.get(`/admin/activity?limit=${limit}`),
};

export default api;
